import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCalendar, IconChevronDown, IconX } from '@tabler/icons-react';
import {
  completeExpenseReceipt,
  createExpense,
  fetchExpenseLinkedTechnicians,
  fetchExpenseTypes,
  presignExpenseReceipt,
  putExpenseReceiptFile,
} from '../services/reembolsosService';
import { PAYMENT_TYPE_LABELS } from '../utils/reembolsosFormat';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import {
  RangeCalendarSingle,
  getCalendarBounds,
  toYmd,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { ymdToBr } from '../../relatorios/utils/relatoriosPeriodDateUtils';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';
import './GestaoReembolsosNewExpenseModal.css';

const PAYMENT_OPTIONS = Object.entries(PAYMENT_TYPE_LABELS).map(([key, label]) => ({
  key,
  label,
}));

const RECEIPT_ACCEPT =
  '.jpg,.jpeg,.jfif,.png,.heic,.pdf,image/jpeg,image/png,image/heic,image/heif,application/pdf';
const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
const RECEIPT_MAX_FILES = 1;
const DESC_MAX = 300;

function todayIsoLocal() {
  return toYmd(new Date());
}

function newDraftId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `d-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Digits → centavos integer → "1.234,56" */
function digitsToBrlMask(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (!d) return '';
  const cents = d.replace(/^0+(?=\d)/, '') || '0';
  const padded = cents.padStart(3, '0');
  const whole = padded.slice(0, -2);
  const frac = padded.slice(-2);
  const wholeNum = Number(whole) || 0;
  return `${wholeNum.toLocaleString('pt-BR')},${frac}`;
}

function parseBrlMaskToNumber(masked) {
  const s = String(masked || '')
    .trim()
    .replace(/\s/g, '')
    .replace(/R\$/i, '');
  if (!s) return NaN;
  if (s.includes(',')) {
    const n = Number(s.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  }
  const digits = s.replace(/\D/g, '');
  if (!digits) return NaN;
  return Number(digits) / 100;
}

function handleAmountInput(raw) {
  return digitsToBrlMask(String(raw || '').replace(/\D/g, ''));
}

function mimeFromFile(file) {
  const t = String(file?.type || '')
    .toLowerCase()
    .split(';')[0]
    .trim();
  if (t) return t === 'image/heif' ? 'image/heic' : t;
  const name = String(file?.name || '').toLowerCase();
  if (/\.jpe?g$|\.jfif$/.test(name)) return 'image/jpeg';
  if (/\.png$/.test(name)) return 'image/png';
  if (/\.heic$|\.heif$/.test(name)) return 'image/heic';
  if (/\.pdf$/.test(name)) return 'application/pdf';
  return '';
}

function isAllowedReceiptFile(file) {
  if (!file) return false;
  const mime = mimeFromFile(file);
  const okMime =
    mime === 'image/jpeg' ||
    mime === 'image/png' ||
    mime === 'image/heic' ||
    mime === 'image/heif' ||
    mime === 'image/jpg' ||
    mime === 'image/jfif' ||
    mime === 'application/pdf';
  const okName = /\.(jpe?g|jfif|png|heic|heif|pdf)$/i.test(file.name || '');
  return okMime || okName;
}

function DayPickerPopover({ anchorRef, selectedDate, onApply, onClose }) {
  const modalRef = useRef(null);
  const [draftDate, setDraftDate] = useState(selectedDate || todayIsoLocal());
  const [position, setPosition] = useState(null);
  const bounds = getCalendarBounds();

  useEffect(() => {
    setDraftDate(selectedDate || todayIsoLocal());
  }, [selectedDate]);

  useLayoutEffect(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const popoverWidth = Math.min(320, window.innerWidth - 24);
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth - 12) {
        left = Math.max(12, window.innerWidth - popoverWidth - 12);
      }
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      if (spaceBelow >= 320) {
        setPosition({ top: rect.bottom + 8, left, bottom: 'auto' });
      } else {
        setPosition({
          bottom: window.innerHeight - rect.top + 8,
          left: Math.max(12, left),
          top: 'auto',
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onDoc = (e) => {
      if (modalRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      if (e.target.closest?.('.ec-period-nav')) return;
      if (e.target.closest?.('.gr-new-exp-day-popover')) return;
      onClose();
    };
    // bubble: evita fechar no mesmo clique que abriu o calendário
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [anchorRef, onClose]);

  if (!position) return null;

  return createPortal(
    <div
      ref={modalRef}
      className="equipment-consult-period-modal equipment-consult-period-modal--no-head ec-module-popover gr-new-exp-day-popover"
      style={{
        position: 'fixed',
        zIndex: 12100,
        width: Math.min(320, window.innerWidth - 24),
        top: position.top === 'auto' ? 'auto' : `${position.top}px`,
        bottom: position.bottom === 'auto' ? 'auto' : `${position.bottom}px`,
        left: `${position.left}px`,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label="Selecionar data"
    >
      <div className="ec-period-picker-body">
        <RangeCalendarSingle
          minDate={bounds.minDate}
          maxDate={bounds.maxDate}
          startDate={draftDate}
          endDate={draftDate}
          onDateSelect={(dateStr) => {
            if (dateStr) setDraftDate(dateStr);
          }}
        />
      </div>
      <div className="ec-period-picker-actions">
        <button
          type="button"
          className="odv2-pill-btn odv2-pill-btn--ghost"
          onClick={() => {
            const today = todayIsoLocal();
            setDraftDate(today);
            onApply(today);
            onClose();
          }}
        >
          Hoje
        </button>
        <button
          type="button"
          className="odv2-pill-btn odv2-pill-btn--primary"
          disabled={!draftDate}
          onClick={() => {
            if (!draftDate) return;
            onApply(draftDate);
            onClose();
          }}
        >
          Aplicar
        </button>
      </div>
    </div>,
    document.body
  );
}

/**
 * Drawer “Nova despesa” — layout do mock + controles padrão do app.
 */
export default function GestaoReembolsosNewExpenseModal({
  open,
  onClose,
  preferredCodtech = null,
  onCreated,
}) {
  const fileRef = useRef(null);
  const techAnchorRef = useRef(null);
  const dateAnchorRef = useRef(null);
  const draftIdRef = useRef(newDraftId());

  const [codtech, setCodtech] = useState('');
  const [expenseDate, setExpenseDate] = useState(todayIsoLocal());
  const [amountStr, setAmountStr] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [paymentType, setPaymentType] = useState('dinheiro_proprio');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [categories, setCategories] = useState([]);
  const [techList, setTechList] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingTechs, setLoadingTechs] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [techOpen, setTechOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [techSearch, setTechSearch] = useState('');
  const [techLayout, setTechLayout] = useState(null);

  const selectedTech = useMemo(
    () => techList.find((t) => String(t.codtech) === String(codtech)) || null,
    [techList, codtech]
  );

  const techItems = useMemo(() => {
    const q = techSearch.trim().toLowerCase();
    return techList
      .filter((t) => {
        if (!q) return true;
        return (
          t.name.toLowerCase().includes(q) ||
          String(t.codtech).toLowerCase().includes(q) ||
          String(t.areaName || '')
            .toLowerCase()
            .includes(q)
        );
      })
      .map((t) => ({
        value: String(t.codtech),
        label: t.name,
        suffix: t.areaName || '',
      }));
  }, [techList, techSearch]);

  const reset = useCallback(() => {
    draftIdRef.current = newDraftId();
    setCodtech('');
    setExpenseDate(todayIsoLocal());
    setAmountStr('');
    setCategoryId('');
    setPaymentType('dinheiro_proprio');
    setDescription('');
    setFiles([]);
    setDragOver(false);
    setError('');
    setNotice('');
    setSaving(false);
    setTechOpen(false);
    setDateOpen(false);
    setTechSearch('');
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    reset();
    const preferred = preferredCodtech != null ? String(preferredCodtech).trim() : '';

    let cancelled = false;
    (async () => {
      setLoadingCats(true);
      setLoadingTechs(true);
      try {
        const [catsData, techs] = await Promise.all([
          fetchExpenseTypes(''),
          fetchExpenseLinkedTechnicians(),
        ]);
        if (cancelled) return;
        const items = (catsData?.items || [])
          .filter((row) => row.active !== false)
          .slice()
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
        setCategories(items);
        if (items.length === 1) setCategoryId(String(items[0].id));
        setTechList(techs);
        if (preferred && techs.some((t) => String(t.codtech) === preferred)) {
          setCodtech(preferred);
        }
      } catch (e) {
        if (!cancelled) {
          setCategories([]);
          setTechList([]);
          setError(e?.message || 'Erro ao carregar dados do formulário');
        }
      } finally {
        if (!cancelled) {
          setLoadingCats(false);
          setLoadingTechs(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, preferredCodtech, reset]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) {
        if (techOpen) {
          setTechOpen(false);
          return;
        }
        if (dateOpen) {
          setDateOpen(false);
          return;
        }
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, saving, techOpen, dateOpen]);

  const updateTechLayout = useCallback(() => {
    const el = techAnchorRef.current;
    if (!el || !techOpen) {
      setTechLayout(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    // Largura igual ao campo "Lançar para" (botão), sem cap artificial de 320
    const width = Math.max(Math.round(rect.width), 240);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    if (left < 8) left = 8;
    let top = rect.bottom + 4;
    let flipped = false;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    if (spaceBelow < 200 && rect.top > 220) {
      top = rect.top - 4;
      flipped = true;
    }
    const maxHeight = Math.min(320, flipped ? rect.top - 16 : spaceBelow);
    setTechLayout({
      position: 'fixed',
      top,
      left,
      width,
      maxHeight: Math.max(180, maxHeight),
      flipped,
      zIndex: 12100,
    });
  }, [techOpen]);

  useLayoutEffect(() => {
    updateTechLayout();
  }, [updateTechLayout, techItems.length]);

  useEffect(() => {
    if (!techOpen) return undefined;
    const onResize = () => updateTechLayout();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [techOpen, updateTechLayout]);

  useEffect(() => {
    if (!techOpen) return undefined;
    const onPointerDown = (event) => {
      const t = event.target;
      if (techAnchorRef.current?.contains(t)) return;
      if (t.closest?.('.board-search-filters-popover')) return;
      setTechOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [techOpen]);

  const removeFile = (localId) => {
    setFiles((prev) => prev.filter((f) => f.localId !== localId));
  };

  const amountNum = parseBrlMaskToNumber(amountStr);
  const amountOk = Number.isFinite(amountNum) && amountNum > 0;
  const canEditAmount = Boolean(codtech);
  const canEditDescription = Boolean(categoryId);
  const descriptionOk = Boolean(String(description || '').trim());
  /** Campos obrigatórios p/ lançar (anexo opcional). */
  const canSubmit =
    Boolean(codtech) && amountOk && Boolean(categoryId) && descriptionOk;
  const canAttach = canSubmit;

  const uploadOne = async (file) => {
    if (!canAttach) {
      setError(
        'Preencha técnico, valor, categoria e descrição antes de anexar o comprovante.'
      );
      return false;
    }
    if (!isAllowedReceiptFile(file)) {
      setError('Anexo inválido. Use JPG, JPEG, JFIF, PNG, HEIC ou PDF.');
      return false;
    }
    if (file.size > RECEIPT_MAX_BYTES) {
      setError('Anexo maior que 10 MB.');
      return false;
    }

    if (files.length >= RECEIPT_MAX_FILES) {
      setError('Já existe um anexo. Remova-o para enviar outro.');
      return false;
    }

    const localId = newDraftId();
    const mime = mimeFromFile(file) || 'image/jpeg';
    setFiles((prev) => {
      if (prev.length >= RECEIPT_MAX_FILES) return prev;
      return [
        ...prev,
        {
          localId,
          name: file.name,
          size: file.size,
          status: 'uploading',
          storageRef: null,
          error: '',
        },
      ];
    });
    setError('');
    setNotice('');

    try {
      const presign = await presignExpenseReceipt({
        draftId: draftIdRef.current,
        mimeType: mime,
        sizeBytes: file.size,
        fileName: file.name,
      });
      await putExpenseReceiptFile(presign.uploadUrl, file, mime);
      const done = await completeExpenseReceipt({
        key: presign.key,
        draftId: draftIdRef.current,
        codtech,
        expenseDate,
      });
      setFiles((prev) =>
        prev.map((f) =>
          f.localId === localId
            ? {
                ...f,
                status: 'done',
                storageRef: done.storageRef || null,
                numeral: done.numeral,
              }
            : f
        )
      );
      return true;
    } catch (e) {
      setFiles((prev) =>
        prev.map((f) =>
          f.localId === localId
            ? { ...f, status: 'failed', error: e?.message || 'Falha no upload' }
            : f
        )
      );
      setError(e?.message || 'Falha ao enviar anexo');
      return false;
    }
  };

  const pickFiles = (fileList) => {
    if (!canAttach) {
      setError(
        'Preencha técnico, valor, categoria e descrição antes de anexar o comprovante.'
      );
      return;
    }
    const arr = Array.from(fileList || []).slice(0, 1);
    if (!arr.length) return;
    void uploadOne(arr[0]);
  };

  const handleSubmit = async () => {
    setError('');
    setNotice('');
    if (!codtech) {
      setError('Selecione o técnico.');
      return;
    }
    const amount = parseBrlMaskToNumber(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Informe um valor maior que zero.');
      return;
    }
    if (!categoryId) {
      setError('Selecione a categoria.');
      return;
    }
    if (!String(description || '').trim()) {
      setError('Informe a descrição.');
      return;
    }
    if (files.some((f) => f.status === 'uploading')) {
      setError('Aguarde o fim do upload dos anexos.');
      return;
    }
    if (files.some((f) => f.status === 'failed')) {
      setError('Remova ou reenvie os anexos com falha.');
      return;
    }

    const receiptStorageKeys = files
      .filter((f) => f.status === 'done' && f.storageRef)
      .map((f) => f.storageRef);

    setSaving(true);
    try {
      await createExpense({
        codtech,
        expenseDate: expenseDate || todayIsoLocal(),
        amount,
        expenseTypeId: categoryId,
        paymentType: paymentType || 'dinheiro_proprio',
        description: description.slice(0, DESC_MAX).trim(),
        receiptStorageKeys,
      });
      onCreated?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Erro ao lançar despesa');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const uploading = files.some((f) => f.status === 'uploading');

  return createPortal(
    <div className="gr-new-exp-overlay" role="presentation">
      <button
        type="button"
        className="gr-new-exp-backdrop"
        aria-label="Fechar"
        onClick={() => !saving && !uploading && onClose?.()}
      />
      <div
        className="gr-new-exp-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gr-new-exp-title"
      >
        <div className="gr-new-exp-head">
          <div>
            <div id="gr-new-exp-title" className="gr-new-exp-title">
              Nova despesa
            </div>
            <div className="gr-new-exp-sub">
              Selecione o técnico responsável pelo lançamento
            </div>
          </div>
          <button
            type="button"
            className="gr-new-exp-close"
            aria-label="Fechar"
            disabled={saving || uploading}
            onClick={() => onClose?.()}
          >
            <IconX size={14} stroke={1.8} />
          </button>
        </div>

        <div className="gr-new-exp-body">
          {error ? <p className="gr-new-exp-error">{error}</p> : null}
          {notice ? <p className="gr-new-exp-notice">{notice}</p> : null}

          <div className="gr-new-exp-field">
            <span className="gr-new-exp-label" id="gr-new-exp-tech-label">
              Lançar para
            </span>
            <div className="gr-new-exp-tech-anchor" ref={techAnchorRef}>
              <button
                type="button"
                id="gr-new-exp-tech"
                className={`gr-new-exp-select-btn${techOpen ? ' is-open' : ''}${
                  selectedTech ? ' has-value' : ''
                }`}
                aria-labelledby="gr-new-exp-tech-label"
                aria-expanded={techOpen}
                aria-haspopup="listbox"
                disabled={saving || loadingTechs}
                onClick={() => {
                  setDateOpen(false);
                  setTechOpen((v) => !v);
                }}
              >
                <span className="gr-new-exp-select-btn-text">
                  {loadingTechs
                    ? 'Carregando técnicos…'
                    : selectedTech
                      ? selectedTech.name
                      : techList.length
                        ? 'Selecione o técnico…'
                        : 'Nenhum técnico vinculado'}
                </span>
                <IconChevronDown size={16} stroke={1.75} aria-hidden />
              </button>
            </div>
            <span className="gr-new-exp-hint">
              Técnicos vinculados à área com coordenador (Equipe).
            </span>
          </div>

          <div className="gr-new-exp-row">
            <div className="gr-new-exp-field">
              <span className="gr-new-exp-label" id="gr-new-exp-date-label">
                Data
              </span>
              <button
                type="button"
                id="gr-new-exp-date"
                ref={dateAnchorRef}
                className={`gr-new-exp-select-btn has-value${dateOpen ? ' is-open' : ''}`}
                aria-labelledby="gr-new-exp-date-label"
                aria-expanded={dateOpen}
                disabled={saving}
                onClick={() => {
                  setTechOpen(false);
                  setDateOpen((v) => !v);
                }}
              >
                <span className="gr-new-exp-select-btn-text">
                  {expenseDate ? ymdToBr(expenseDate) : 'Selecionar'}
                </span>
                <IconCalendar size={15} stroke={1.6} aria-hidden />
              </button>
            </div>
            <div className="gr-new-exp-field">
              <label className="gr-new-exp-label" htmlFor="gr-new-exp-amount">
                Valor (R$)
              </label>
              <input
                id="gr-new-exp-amount"
                className="gr-new-exp-input is-amount"
                placeholder="0,00"
                value={amountStr}
                disabled={saving || !canEditAmount}
                inputMode="numeric"
                aria-disabled={!canEditAmount}
                onChange={(e) => {
                  if (!canEditAmount) return;
                  setAmountStr(handleAmountInput(e.target.value));
                }}
              />
              {!canEditAmount ? (
                <span className="gr-new-exp-hint">Selecione o técnico para informar o valor.</span>
              ) : null}
            </div>
          </div>

          <div className="gr-new-exp-field">
            <span className="gr-new-exp-label">Categoria</span>
            <div className="gr-new-exp-chips" role="group" aria-label="Categoria">
              {loadingCats ? (
                <span className="gr-new-exp-hint">Carregando…</span>
              ) : categories.length ? (
                categories.map((c) => {
                  const id = String(c.id);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`gr-new-exp-chip${categoryId === id ? ' is-active' : ''}`}
                      disabled={saving}
                      onClick={() => setCategoryId(id)}
                    >
                      {c.name || '—'}
                    </button>
                  );
                })
              ) : (
                <span className="gr-new-exp-hint">Nenhuma categoria ativa.</span>
              )}
            </div>
          </div>

          <div className="gr-new-exp-field">
            <span className="gr-new-exp-label">Tipo de pagamento</span>
            <div className="gr-new-exp-chips" role="group" aria-label="Tipo de pagamento">
              {PAYMENT_OPTIONS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`gr-new-exp-chip${paymentType === p.key ? ' is-active' : ''}`}
                  disabled={saving}
                  onClick={() => setPaymentType(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="gr-new-exp-field">
            <label className="gr-new-exp-label" htmlFor="gr-new-exp-desc">
              Descrição
            </label>
            <input
              id="gr-new-exp-desc"
              className="gr-new-exp-input"
              placeholder="Ex.: Táxi aeroporto → cliente"
              value={description}
              disabled={saving || !canEditDescription}
              maxLength={DESC_MAX}
              aria-disabled={!canEditDescription}
              onChange={(e) => {
                if (!canEditDescription) return;
                setDescription(e.target.value.slice(0, DESC_MAX));
              }}
            />
            {canEditDescription ? (
              <span className="gr-new-exp-hint">
                {description.length}/{DESC_MAX}
              </span>
            ) : (
              <span className="gr-new-exp-hint">Selecione a categoria para informar a descrição.</span>
            )}
          </div>

          <div className="gr-new-exp-field">
            <span className="gr-new-exp-label">Anexos</span>
            <div
              className={`gr-new-exp-drop${dragOver && canAttach ? ' is-drag' : ''}${
                !canAttach || saving ? ' is-disabled' : ''
              }`}
              role="button"
              tabIndex={canAttach && !saving ? 0 : -1}
              aria-disabled={!canAttach || saving}
              onKeyDown={(e) => {
                if (!canAttach || saving) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileRef.current?.click();
                }
              }}
              onClick={() => {
                if (!canAttach || saving) {
                  if (!canAttach) {
                    setError(
                      'Preencha técnico, valor, categoria e descrição antes de anexar o comprovante.'
                    );
                  }
                  return;
                }
                fileRef.current?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (!canAttach || saving) return;
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (!canAttach || saving) {
                  setError(
                    'Preencha técnico, valor, categoria e descrição antes de anexar o comprovante.'
                  );
                  return;
                }
                pickFiles(e.dataTransfer?.files);
              }}
            >
              <div className="gr-new-exp-drop-title">Arraste o comprovante aqui</div>
              <div className="gr-new-exp-drop-sub">
                JPG, JPEG, JFIF, PNG, HEIC ou PDF · até 10 MB · 1 arquivo
              </div>
            </div>
            {!canAttach ? (
              <span className="gr-new-exp-hint">
                Disponibilizado após técnico, valor, categoria e descrição.
              </span>
            ) : null}
            {files.length ? (
              <ul className="gr-new-exp-file-list">
                {files.map((f) => (
                  <li key={f.localId} className={`gr-new-exp-file-item is-${f.status}`}>
                    <span className="gr-new-exp-file-name" title={f.name}>
                      {f.name}
                    </span>
                    <span className="gr-new-exp-file-status">
                      {f.status === 'uploading'
                        ? 'Enviando…'
                        : f.status === 'done'
                          ? 'Enviado'
                          : f.error || 'Falhou'}
                    </span>
                    <button
                      type="button"
                      className="gr-new-exp-file-remove"
                      disabled={saving || f.status === 'uploading'}
                      aria-label={`Remover ${f.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(f.localId);
                      }}
                    >
                      <IconX size={12} stroke={2} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <input
              ref={fileRef}
              type="file"
              className="gr-new-exp-file-input"
              accept={RECEIPT_ACCEPT}
              onChange={(e) => {
                pickFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="gr-new-exp-foot">
          <span className="gr-new-exp-status-hint">
            Entrará como <b>Pendente</b>
          </span>
          <div className="gr-new-exp-actions">
            <button
              type="button"
              className="user-form-link-btn user-form-footer-btn"
              disabled={saving || uploading}
              onClick={() => onClose?.()}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="user-form-profile-save-btn user-form-footer-btn"
              disabled={saving || uploading || !canSubmit}
              onClick={handleSubmit}
            >
              Lançar despesa
            </button>
          </div>
        </div>
      </div>

      {techOpen && techLayout
        ? createPortal(
            <div
              className="board-search-filters-popover board-search-filters-popover--simple board-search-filters-popover--categoria gr-module-popover gr-new-exp-tech-popover"
              style={{
                position: techLayout.position,
                top: techLayout.top,
                left: techLayout.left,
                width: techLayout.width,
                maxHeight: techLayout.maxHeight,
                zIndex: techLayout.zIndex,
                boxSizing: 'border-box',
                transform: techLayout.flipped ? 'translateY(-100%)' : undefined,
                '--bsf-cidade-popover-max-h': `${techLayout.maxHeight}px`,
              }}
              role="dialog"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <BoardSearchSimplePickerPanel
                searchPlaceholder="Buscar técnico"
                showSearch
                searchTerm={techSearch}
                onSearchChange={setTechSearch}
                items={techItems}
                getKey={(i) => i.value}
                getLabel={(i) => i.label}
                getSuffix={(i) => i.suffix}
                selectedKeys={codtech ? [String(codtech)] : []}
                onToggle={(item) => {
                  setCodtech(String(item.value));
                  setTechOpen(false);
                  setTechSearch('');
                }}
                emptyMessage="Nenhum técnico encontrado"
                listAriaLabel="Técnicos"
              />
            </div>,
            document.body
          )
        : null}

      {dateOpen ? (
        <DayPickerPopover
          anchorRef={dateAnchorRef}
          selectedDate={expenseDate}
          onApply={setExpenseDate}
          onClose={() => setDateOpen(false)}
        />
      ) : null}
    </div>,
    document.body
  );
}
